import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@/hooks/useTranslation';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import LanguageSelector from '@/components/language-selector';
import { useLanguage } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';
import { useGeo } from '@/hooks/useGeo';
import { countryToCurrency, formatLandingAmount } from '@/lib/geo';
import { Menu, X, Check, Gift, Users, Star, Shield, Clock, ArrowRight, Mail, Sparkles, Heart, Lock, Loader2, Target, CalendarDays, TrendingUp } from 'lucide-react';
import { GratimyLogo } from '@/components/ui/gratimy-logo';
import SiteFooter from '@/components/SiteFooter';
import gratimyDashboardTilted from '../assets/gratimy-dashboard.png';

type ReviewItem = {
  id: string;
  rating: number;
  body: string;
  isFeatured: boolean;
  isAnonymous: boolean;
  displayName: string | null;
  profileImageUrl: string | null;
  role: string | null;
  createdAt: string | null;
};

type BubbleVariant = 'blue' | 'pink' | 'gradient';

const bubbleConfigs = [
  { size: 70,  top: '14%', left: '8%',  speed: 0.10, mobileHide: true, variant: 'blue' as BubbleVariant },
  { size: 110, top: '22%', left: '80%', speed: 0.08, mobileHide: true, variant: 'pink' as BubbleVariant },
  { size: 90,  top: '60%', left: '6%',  speed: 0.12, mobileHide: true, variant: 'gradient' as BubbleVariant },
  { size: 60,  top: '68%', left: '86%', speed: 0.14, mobileHide: true, variant: 'pink' as BubbleVariant },
];

const BUBBLE_IMAGES: Record<BubbleVariant, string> = {
  blue: '/gratimy-icon-blue.webp',
  pink: '/gratimy-icon-pink.webp',
  gradient: '/gratimy-icon.webp',
};

const heroExtraBubbles: Array<{ size: number; top: string; left: string; speed: number; variant: BubbleVariant }> = [];

const tabletBubbles = [
  { size: 60, top: '14%', left: '6%',  variant: 'blue' as BubbleVariant, speed: 0.08 },
  { size: 75, top: '20%', left: '82%', variant: 'pink' as BubbleVariant, speed: 0.10 },
  { size: 50, top: '62%', left: '8%',  variant: 'gradient' as BubbleVariant, speed: 0.10 },
];

const mobileBubbles = [
  { size: 50, top: '12%', left: '6%',  variant: 'blue' as BubbleVariant, speed: 0.06 },
  { size: 45, top: '18%', left: '78%', variant: 'pink' as BubbleVariant, speed: 0.10 },
];

const sectionBubbles = [
  { size: 80, top: '8%', left: '85%', variant: 'pink' as BubbleVariant, speed: 0.04 },
  { size: 60, top: '60%', left: '3%', variant: 'blue' as BubbleVariant, speed: 0.07 },
  { size: 70, top: '15%', left: '5%', variant: 'blue' as BubbleVariant, speed: 0.06 },
  { size: 55, top: '70%', left: '88%', variant: 'pink' as BubbleVariant, speed: 0.08 },
  { size: 45, top: '50%', left: '2%', variant: 'gradient' as BubbleVariant, speed: 0.05 },
  { size: 70, top: '38%', left: '6%', variant: 'pink' as BubbleVariant, speed: 0.05 },
  { size: 60, top: '62%', left: '90%', variant: 'blue' as BubbleVariant, speed: 0.06 },
];

interface BubbleProps {
  label?: string;
  size: number;
  top: string;
  left: string;
  speed: number;
  mobileHide?: boolean;
  variant: BubbleVariant;
  visibilityClass?: string;
  parallaxRef: React.RefObject<HTMLDivElement | null>;
}

function ParallaxBubble({ label, size, top, left, mobileHide, variant, visibilityClass, parallaxRef }: BubbleProps) {
  const hideClass = visibilityClass || (mobileHide ? 'hidden lg:flex' : 'flex');
  return (
    <div
      ref={parallaxRef as React.RefObject<HTMLDivElement>}
      className={`absolute pointer-events-none select-none items-center justify-center ${hideClass}`}
      style={{ top, left, width: size, height: size }}
    >
      <div className="relative" style={{ width: size, height: size }}>
        <img
          src={BUBBLE_IMAGES[variant]}
          alt=""
          className="w-full h-full object-contain rounded-full"
          loading="lazy"
          draggable={false}
        />
        {label && (
          <span
            className="absolute inset-0 hidden lg:flex items-center justify-center text-white/90 font-semibold text-center font-montserrat"
            style={{ fontSize: size * 0.13, textShadow: '0 1px 6px rgba(61,31,122,0.3)' }}
          >
            {label}
          </span>
        )}
      </div>
    </div>
  );
}

const SHOW_WAITLIST_OVERLAY = false;

function WaitlistOverlay() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage } = useLanguage();
  const emailRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (visible && emailRef.current) {
      emailRef.current.focus();
    }
  }, [visible]);

  useEffect(() => {
    if (!visible || !dialogRef.current) return;
    const dialog = dialogRef.current;
    const focusableEls = dialog.querySelectorAll<HTMLElement>(
      'input, button, [tabindex]:not([tabindex="-1"])'
    );
    const firstEl = focusableEls[0];
    const lastEl = focusableEls[focusableEls.length - 1];

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || focusableEls.length === 0) return;
      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl?.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, submitted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch('/api/newsletter/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          gdprConsent: true,
          marketingConsent: true,
          source: 'waitlist',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed');
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('landing.newsletterErrorDesc'));
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-700"
      style={{
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        background: 'linear-gradient(135deg, rgba(111, 142, 220, 0.75) 0%, rgba(214, 137, 172, 0.75) 100%)',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="waitlist-title"
    >
      <div className="absolute top-4 right-4 z-10 flex gap-1">
        <button
          type="button"
          onClick={() => changeLanguage('da')}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${currentLanguage === 'da' ? 'bg-white/70 text-[#3D1F7A]' : 'text-white/80 hover:bg-white/20'}`}
        >
          DA
        </button>
        <button
          type="button"
          onClick={() => changeLanguage('en')}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${currentLanguage === 'en' ? 'bg-white/70 text-[#3D1F7A]' : 'text-white/80 hover:bg-white/20'}`}
        >
          EN
        </button>
      </div>
      <div
        ref={dialogRef}
        className="w-full max-w-md mx-4 p-8 sm:p-10 text-center animate-in fade-in slide-in-from-bottom-4 duration-500"
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.55) 0%, rgba(244, 236, 251, 0.55) 100%)',
          backdropFilter: 'blur(24px) saturate(140%)',
          WebkitBackdropFilter: 'blur(24px) saturate(140%)',
          border: '1.5px solid rgba(255, 255, 255, 0.6)',
          borderRadius: '28px',
          boxShadow: 'inset 0 1px 2px rgba(255, 255, 255, 0.85), 0 16px 48px rgba(124, 159, 247, 0.22)',
        }}
      >
        <GratimyLogo variant="full" size={40} className="mx-auto mb-6 justify-center" />
        <h2 id="waitlist-title" className="text-3xl font-bold mb-3 font-ztgatha" style={{ color: '#3D1F7A' }}>
          {t('waitlist.comingSoon')}
        </h2>
        <p className="text-sm mb-6 leading-relaxed font-montserrat" style={{ color: '#6B46C1' }}>
          {t('waitlist.subtitle')}
        </p>

        {submitted ? (
          <div className="py-4">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'linear-gradient(135deg, #7C9FF7, #6B46C1)' }}
            >
              <Check className="w-7 h-7 text-white" />
            </div>
            <h3 className="text-lg font-semibold mb-1 font-ztgatha" style={{ color: '#3D1F7A' }}>{t('waitlist.thankYou')}</h3>
            <p className="text-sm font-montserrat" style={{ color: '#6B46C1' }}>{t('waitlist.thankYouDesc')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <label htmlFor="waitlist-email" className="sr-only">
              {t('waitlist.emailPlaceholder')}
            </label>
            <Input
              id="waitlist-email"
              ref={emailRef}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('waitlist.emailPlaceholder')}
              className="h-12 rounded-xl text-center text-sm focus:ring-2"
              style={{
                background: 'rgba(255, 255, 255, 0.75)',
                border: '1px solid rgba(124, 159, 247, 0.35)',
                color: '#3D1F7A',
              }}
              aria-describedby={error ? 'waitlist-error' : undefined}
            />
            {error && (
              <p id="waitlist-error" className="text-xs" style={{ color: '#FB7185' }} role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={submitting || !email}
              className="w-full h-12 rounded-full text-white font-semibold text-sm bg-[#3B82F6] hover:bg-[#2563EB]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('waitlist.signing')}
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  {t('waitlist.signup')}
                </>
              )}
            </Button>
          </form>
        )}

        <p className="text-xs mt-4 font-montserrat" style={{ color: '#8B5CF6' }}>{t('waitlist.privacy')}</p>
      </div>
    </div>
  );
}

function ImpactSection() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [tab, setTab] = useState<'goal' | 'stock'>('goal');

  // Default numeric examples are still chosen by language so the round
  // numbers feel native to the copy (Danish copy talks about 3500/250,
  // English copy about 499/35). The *currency* marker, however, comes
  // from the visitor's geo-resolved currency so a German visitor reading
  // English copy still sees "€499", not "$499".
  const isDanish = currentLanguage === 'da';
  const { data: geo } = useGeo();
  const currency = countryToCurrency(geo?.country);
  const goalTarget = isDanish ? 3500 : 499;
  const defaultGiftAmount = isDanish ? 250 : 35;

  const [giftAmount, setGiftAmount] = useState<number>(defaultGiftAmount);
  useEffect(() => {
    setGiftAmount(defaultGiftAmount);
  }, [defaultGiftAmount]);

  const fmt = (n: number) => formatLandingAmount(Math.round(n), currency);

  const safeGift = Number.isFinite(giftAmount) && giftAmount > 0 ? giftAmount : 0;

  // Goal calc
  const progressPct = Math.min(100, Math.max(0, (safeGift / goalTarget) * 100));

  // Stock calc — S&P 500 long-term avg ~10%, 18 years
  const STOCK_RATE = 10;
  const STOCK_YEARS = 18;
  const futureValue = safeGift * Math.pow(1 + STOCK_RATE / 100, STOCK_YEARS);
  const growthPct = safeGift > 0 ? ((futureValue / safeGift) - 1) * 100 : 0;

  const Pill = ({ active, onClick, children, testid }: { active: boolean; onClick: () => void; children: React.ReactNode; testid: string }) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testid}
      className="inline-flex items-center justify-center px-5 h-11 rounded-full font-montserrat font-semibold text-sm transition-all"
      style={
        active
          ? {
              background: '#3B82F6',
              color: '#fff',
              boxShadow: '0 10px 24px rgba(59,130,246,0.35)',
              border: '1px solid #3B82F6',
            }
          : {
              background: 'rgba(255,255,255,0.7)',
              color: 'var(--color-violet-900)',
              border: '1.5px solid rgba(255,255,255,0.95)',
              boxShadow: '0 6px 16px rgba(124,159,247,0.18)',
            }
      }
    >
      {children}
    </button>
  );

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.55)',
    border: '1.5px solid rgba(255,255,255,0.9)',
    borderRadius: 24,
    boxShadow: '0 14px 36px rgba(124,159,247,0.22), inset 0 1px 1px rgba(255,255,255,0.7)',
  };

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.9)',
    border: '1.5px solid rgba(255,255,255,1)',
    borderRadius: 14,
    color: 'var(--color-violet-900)',
  };

  return (
    <section
      id="impact"
      className="relative py-20 md:py-28 px-4 md:px-8 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #DCD4F2 0%, #E2D5F1 30%, #F0E1ED 65%, #FFF4FA 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="md:grid md:grid-cols-2 md:gap-14 lg:gap-20 md:items-center">
          {/* ===== Left: heading + tabs + support copy ===== */}
          <div className="text-center md:text-left max-w-md md:max-w-none mx-auto md:mx-0 mb-10 md:mb-0">
            <h2
              className="font-ztgatha font-bold text-4xl md:text-5xl mb-5 leading-tight"
              style={{ color: 'var(--color-violet-900)' }}
            >
              {t('landing.impact.title')}
            </h2>
            <p
              className="font-montserrat text-base mb-7 leading-relaxed"
              style={{ color: 'var(--color-violet-500)' }}
            >
              {t('landing.impact.subtitle')}
            </p>

            <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-6">
              <Pill active={tab === 'goal'} onClick={() => setTab('goal')} testid="button-impact-tab-goal">
                <Target className="w-4 h-4 mr-2" strokeWidth={2} />
                {t('landing.impact.tabGoal')}
              </Pill>
              <Pill active={tab === 'stock'} onClick={() => setTab('stock')} testid="button-impact-tab-stock">
                <TrendingUp className="w-4 h-4 mr-2" strokeWidth={2} />
                {t('landing.impact.tabStock')}
              </Pill>
            </div>

            <p
              className="font-montserrat text-sm leading-relaxed"
              style={{ color: 'var(--color-violet-500)' }}
              data-testid="text-impact-support"
            >
              {tab === 'goal' ? t('landing.impact.goalSupport') : t('landing.impact.stockSupport')}
            </p>
          </div>

          {/* ===== Right: interactive widget ===== */}
          <div className="relative" data-testid={`widget-impact-${tab}`}>
            {tab === 'goal' ? (
              <div className="p-7 md:p-8 backdrop-blur-md" style={cardStyle}>
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(59,130,246,0.12)' }}
                  >
                    <Target className="w-5 h-5" style={{ color: '#3B82F6' }} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-montserrat text-xs uppercase tracking-wider" style={{ color: 'var(--color-violet-500)' }}>
                      {t('landing.impact.goalLabel')}
                    </div>
                    <div className="font-ztgatha font-semibold text-lg leading-tight truncate" style={{ color: 'var(--color-violet-900)' }}>
                      {t('landing.impact.goalName')}
                    </div>
                  </div>
                  <div className="text-right font-ztgatha font-bold text-lg" style={{ color: 'var(--color-violet-900)' }}>
                    {fmt(goalTarget)}
                  </div>
                </div>

                <label htmlFor="impact-goal-amount" className="block font-montserrat text-sm font-semibold mb-2" style={{ color: 'var(--color-violet-700)' }}>
                  {t('landing.impact.yourGift')}
                </label>
                <div className="relative mb-5">
                  <input
                    id="impact-goal-amount"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={giftAmount === 0 ? '' : giftAmount}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setGiftAmount(Number.isFinite(v) ? Math.max(0, v) : 0);
                    }}
                    placeholder={t('landing.impact.amountPlaceholder')}
                    className="w-full h-12 px-4 pr-16 font-montserrat font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                    style={inputStyle}
                    data-testid="input-impact-goal-amount"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 font-montserrat text-sm" style={{ color: 'var(--color-violet-500)' }}>
                    {currency}
                  </span>
                </div>

                <div
                  className="w-full h-3 rounded-full overflow-hidden mb-3"
                  style={{ background: 'rgba(124,159,247,0.18)' }}
                >
                  <div
                    className="h-full transition-all duration-500 ease-out"
                    style={{
                      width: `${progressPct}%`,
                      background: 'linear-gradient(90deg, #3B82F6 0%, #6B46C1 100%)',
                      boxShadow: '0 0 12px rgba(59,130,246,0.45)',
                    }}
                    data-testid="bar-impact-goal-progress"
                  />
                </div>
                <div className="flex justify-between font-montserrat text-sm" style={{ color: 'var(--color-violet-700)' }}>
                  <span className="font-semibold" data-testid="text-impact-goal-current">{fmt(safeGift)}</span>
                  <span><span className="font-semibold" style={{ color: '#3B82F6' }}>{Math.round(progressPct)}%</span> {t('landing.impact.goalOf')}</span>
                </div>
              </div>
            ) : (
              <div className="p-7 md:p-8 backdrop-blur-md" style={cardStyle}>
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(59,130,246,0.12)' }}
                  >
                    <TrendingUp className="w-5 h-5" style={{ color: '#3B82F6' }} strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-montserrat text-xs uppercase tracking-wider" style={{ color: 'var(--color-violet-500)' }}>
                      {t('landing.impact.stockReturn')}
                    </div>
                    <div className="font-ztgatha font-semibold text-lg leading-tight" style={{ color: 'var(--color-violet-900)' }}>
                      {STOCK_RATE.toFixed(1)}% · {STOCK_YEARS} {t('landing.impact.stockYearsUnit')}
                    </div>
                  </div>
                </div>

                <label htmlFor="impact-stock-amount" className="block font-montserrat text-sm font-semibold mb-2" style={{ color: 'var(--color-violet-700)' }}>
                  {t('landing.impact.yourGift')}
                </label>
                <div className="relative mb-6">
                  <input
                    id="impact-stock-amount"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={giftAmount === 0 ? '' : giftAmount}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setGiftAmount(Number.isFinite(v) ? Math.max(0, v) : 0);
                    }}
                    placeholder={t('landing.impact.amountPlaceholder')}
                    className="w-full h-12 px-4 pr-16 font-montserrat font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-[#3B82F6]/40"
                    style={inputStyle}
                    data-testid="input-impact-stock-amount"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 font-montserrat text-sm" style={{ color: 'var(--color-violet-500)' }}>
                    {currency}
                  </span>
                </div>

                <div
                  className="rounded-2xl p-5 text-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.10) 0%, rgba(107,70,193,0.12) 100%)',
                    border: '1px solid rgba(255,255,255,0.9)',
                  }}
                >
                  <div className="font-montserrat text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--color-violet-500)' }}>
                    {t('landing.impact.stockFutureValue')}
                  </div>
                  <div
                    className="font-ztgatha font-bold text-3xl md:text-4xl mb-1 leading-tight"
                    style={{ color: 'var(--color-violet-900)' }}
                    data-testid="text-impact-stock-future"
                  >
                    {fmt(futureValue)}
                  </div>
                  <div className="font-montserrat text-sm font-semibold" style={{ color: '#3B82F6' }}>
                    +{Math.round(growthPct)}% {t('landing.impact.stockGrowth')}
                  </div>
                </div>

                <p
                  className="mt-4 font-montserrat text-xs leading-relaxed text-center"
                  style={{ color: 'var(--color-violet-500)' }}
                  data-testid="text-impact-stock-disclaimer"
                >
                  {t('landing.impact.stockDisclaimer')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// Swipeable carousel for the mobile-only sections that previously
// showed a single static item with decorative dots. Each child becomes
// a full-width snap slide; dots are clickable and reflect the active
// slide based on scroll position.
function MobileCarousel({
  items,
  ariaLabel,
  slideAriaLabel,
}: {
  items: React.ReactNode[];
  ariaLabel?: string;
  slideAriaLabel?: (i: number) => string;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => {
      const width = el.clientWidth;
      if (!width) return;
      const idx = Math.round(el.scrollLeft / width);
      setActiveIndex(Math.max(0, Math.min(items.length - 1, idx)));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [items.length]);

  const scrollTo = (idx: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * idx, behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      <div
        ref={scrollerRef}
        role="region"
        aria-label={ariaLabel}
        className="flex overflow-x-auto snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
      >
        {items.map((item, i) => (
          <div key={i} className="snap-center shrink-0 w-full">
            {item}
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center justify-center gap-1.5">
        {items.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={slideAriaLabel ? slideAriaLabel(i) : `Slide ${i + 1}`}
            aria-current={i === activeIndex ? 'true' : undefined}
            onClick={() => scrollTo(i)}
            className="rounded-full transition-all duration-200"
            style={{
              width: i === activeIndex ? 24 : 6,
              height: 6,
              background: i === activeIndex ? 'var(--color-violet-700)' : 'rgba(107,70,193,0.25)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ReviewCard({
  review,
  t,
}: {
  review: ReviewItem;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const stars = Math.max(1, Math.min(5, review.rating));
  const name = review.isAnonymous || !review.displayName
    ? t('landing.reviews.anonymous')
    : review.displayName;
  const photo = review.isAnonymous ? null : review.profileImageUrl;
  const role = review.role;
  const ratingDisplay = stars.toFixed(1);

  // Photo-overlay card matching the design-system "Photo Card Gradient
  // Overlay" pattern (design-system §6): full-bleed photo with a
  // transparent → violet fade that keeps text readable without using
  // any black. Anonymous reviews fall back to the brand pastel gradient
  // surface from the same spec.
  return (
    <div
      data-review-card
      data-testid={`review-card-${review.id}`}
      className="relative flex-shrink-0 snap-start overflow-hidden w-[82vw] min-w-[260px] max-w-[340px] md:w-[320px] md:max-w-[340px]"
      style={{
        borderRadius: 24,
        boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.18), 0 12px 32px rgba(107,70,193,0.28)',
        height: 420,
      }}
    >
      {photo ? (
        <img
          src={photo}
          alt={t('landing.reviews.imageAlt', { name })}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      ) : (
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(145deg, #7C9FF7 0%, #BCAEF0 40%, #FFCAE2 70%, #FFD6EB 100%)' }}
        />
      )}

      {/* Violet fade overlay — transparent at the top, semi-opaque at the
          bottom — so the quote and attribution stay legible over any
          underlying photo. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'linear-gradient(to bottom, rgba(61,31,122,0) 0%, rgba(61,31,122,0) 30%, rgba(61,31,122,0.55) 65%, rgba(61,31,122,0.88) 100%)',
        }}
      />

      {/* Rating chip — top-left, sits on the lighter (transparent) part
          of the fade so we give it its own pill for contrast. */}
      <div
        className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 rounded-full"
        style={{
          background: 'rgba(61,31,122,0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        <Star className="w-3.5 h-3.5 fill-[#FFE45F] text-[#FFE45F]" />
        <span
          className="font-montserrat font-semibold text-white text-xs"
          style={{ textShadow: '0 1px 3px rgba(61,31,122,0.5)' }}
        >
          {ratingDisplay}
        </span>
        <span className="flex gap-0.5 ml-0.5">
          {Array.from({ length: stars - 1 }, (_, i) => (
            <Star key={i} className="w-3 h-3 fill-[#FFE45F] text-[#FFE45F]" />
          ))}
        </span>
      </div>

      {/* Content sits at the bottom over the fade. */}
      <div
        className="absolute bottom-0 left-0 right-0 p-6 flex flex-col gap-3"
        style={{ color: 'white' }}
      >
        <p
          className="text-[15px] leading-relaxed font-montserrat font-medium"
          style={{ textShadow: '0 1px 4px rgba(61,31,122,0.45)' }}
        >
          {review.body}
        </p>
        <div className="pt-3 border-t border-white/25">
          <div
            className="font-ztgatha font-semibold text-[16px] text-white"
            style={{ textShadow: '0 1px 4px rgba(61,31,122,0.4)' }}
          >
            {name}
          </div>
          {role && (
            <div
              className="font-montserrat font-medium text-xs text-white/85 mt-0.5"
              style={{ textShadow: '0 1px 3px rgba(61,31,122,0.4)' }}
            >
              {role}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [, navigate] = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const { user, isLoading } = useAuth();

  // Geo → currency for the illustrative investment example. The
  // example numbers (500 / 2029) are kept constant so the relative
  // growth still tells the same story, but the symbol and number
  // formatting follow the visitor's local currency: DKK for Denmark,
  // GBP for the UK, EUR for the rest of the EU, and USD elsewhere.
  const { data: geo } = useGeo();
  const landingCurrency = countryToCurrency(geo?.country);
  const startAmount = formatLandingAmount(500, landingCurrency);
  const endAmount = formatLandingAmount(2029, landingCurrency);

  // Reviews — DB-backed, replaces the previous static 3-card section.
  const { data: reviewsData, isError: reviewsError } = useQuery<ReviewItem[]>({
    queryKey: ['/api/reviews'],
  });
  // Translation-driven fallback so first paint always shows the three
  // featured testimonial cards in their original visual style and in the
  // current locale (EN/DA). Once the API resolves, it replaces this with
  // the live list (which includes the same featured rows + user reviews).
  const featuredFallback = useMemo<ReviewItem[]>(() => [
    {
      id: 'featured-fallback-sarah',
      rating: 5,
      body: t('landing.testimonial1Quote'),
      isFeatured: true,
      isAnonymous: false,
      displayName: t('landing.testimonial1Name'),
      profileImageUrl: '/testimonials/sarah.webp',
      role: t('landing.testimonial1Role'),
      createdAt: null,
    },
    {
      id: 'featured-fallback-mike',
      rating: 5,
      body: t('landing.testimonial2Quote'),
      isFeatured: true,
      isAnonymous: false,
      displayName: t('landing.testimonial2Name'),
      profileImageUrl: '/testimonials/mike.webp',
      role: t('landing.testimonial2Role'),
      createdAt: null,
    },
    {
      id: 'featured-fallback-lisa',
      rating: 5,
      body: t('landing.testimonial3Quote'),
      isFeatured: true,
      isAnonymous: false,
      displayName: t('landing.testimonial3Name'),
      profileImageUrl: '/testimonials/lisa.webp',
      role: t('landing.testimonial3Role'),
      createdAt: null,
    },
  ], [t, language]);
  // Always render the bilingual curated cards first; append user-submitted
  // reviews from the API after they load. This guarantees DA users never see
  // English curated content swapped in after the query resolves.
  const reviews = useMemo<ReviewItem[]>(
    () => [...featuredFallback, ...(reviewsData ?? [])],
    [reviewsData, featuredFallback],
  );
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const reviewsScrollerRef = useRef<HTMLDivElement | null>(null);
  const scrollReviews = (dir: 1 | -1) => {
    const el = reviewsScrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-review-card]');
    const step = card ? card.getBoundingClientRect().width + 24 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard');
    }
  }, [user, isLoading, navigate]);

  const allBubbleConfigs = [
    ...bubbleConfigs.map(b => ({ ...b })),
    ...heroExtraBubbles.map(b => ({ ...b, mobileHide: true })),
    ...tabletBubbles.map(b => ({ ...b, mobileHide: false })),
    ...mobileBubbles.map(b => ({ ...b, mobileHide: false })),
    ...sectionBubbles.map(b => ({ ...b, mobileHide: false })),
  ];

  const bubbleRefs = useRef<Array<React.RefObject<HTMLDivElement | null>>>(
    allBubbleConfigs.map(() => React.createRef<HTMLDivElement>())
  );

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          bubbleRefs.current.forEach((ref, i) => {
            if (ref.current && allBubbleConfigs[i]) {
              const offset = scrollY * allBubbleConfigs[i].speed;
              ref.current.style.transform = `translateY(${offset}px)`;
            }
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNewsletterSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setSubscribing(true);
    try {
      const response = await fetch('/api/newsletter/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: newsletterEmail,
          gdprConsent: true,
          marketingConsent: true,
          source: 'landing_page',
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to subscribe');
      toast({ title: t('landing.newsletterSuccess'), description: t('landing.newsletterSuccessDesc') });
      setNewsletterEmail('');
    } catch (error) {
      toast({ title: t('landing.newsletterError'), description: error instanceof Error ? error.message : t('landing.newsletterErrorDesc'), variant: 'destructive' });
    } finally {
      setSubscribing(false);
    }
  };

  const seoTitle = language === 'da'
    ? 'Gratimy - Digital gavebog & børneopsparing | Kærlige minder til barnets fremtid'
    : 'Gratimy - Digital Gift Journal & Savings App | Loving Memories for Kids';
  const seoDescription = language === 'da'
    ? 'Skab en digital gavebog med kærlige budskaber, livsråd og økonomiske bidrag til dit barns fremtid. Gratimy samler meningsfulde gaver, minder og børneopsparing ét sted.'
    : 'Create a digital gift journal with loving messages, life advice, and financial gifts for your child\'s future. Gratimy combines meaningful gifts, memory books, and children\'s savings in one place.';

  const organizationJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Gratimy',
    url: 'https://gratimy.com',
    logo: 'https://gratimy.com/gratimy-logo.png',
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'support@gratimy.com',
      contactType: 'customer support',
    },
  };

  const softwareAppJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Gratimy',
    applicationCategory: ['FinanceApplication', 'LifestyleApplication'],
    operatingSystem: 'Web',
    description: language === 'da'
      ? 'Digital gavebog og børneopsparingsapp. Saml kærlige budskaber, livsråd og økonomiske gaver til dit barns fremtid.'
      : 'Digital gift journal and children\'s savings app. Collect loving messages, life advice, and financial gifts for your child\'s future.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'DKK',
      description: language === 'da' ? 'Gratis plan med 1 bog' : 'Free plan with 1 book',
    },
    url: 'https://gratimy.com',
  };

  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonicalPath="/"
        jsonLd={[organizationJsonLd, softwareAppJsonLd]}
      />
      {SHOW_WAITLIST_OVERLAY && !user && !isLoading && <WaitlistOverlay />}

      <main>
      {/* ===== HERO SECTION — Figma 2026 redesign: blue→pink gradient with blurred backdrop bubbles ===== */}
      <div className="gradient-hero relative overflow-hidden flex flex-col" style={{ minHeight: '100vh' }}>

        {/* Decorative large blurred backdrop bubbles (static, non-parallax) */}
        <img
          src="/gratimy-icon.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute z-0"
          style={{
            width: 'min(720px, 75vw)',
            height: 'min(720px, 75vw)',
            top: '-12%',
            left: '-10%',
            opacity: 0.35,
            filter: 'blur(60px)',
          }}
          draggable={false}
        />
        <img
          src="/gratimy-icon-pink.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute z-0"
          style={{
            width: 'min(520px, 60vw)',
            height: 'min(520px, 60vw)',
            bottom: '-8%',
            right: '-8%',
            opacity: 0.18,
            filter: 'blur(40px)',
          }}
          draggable={false}
        />

        {/* Parallax bubbles — sharper foreground decorations */}
        <div className="absolute inset-0 z-[1]">
          {bubbleConfigs.map((b, i) => (
            <ParallaxBubble
              key={`hero-bubble-${i}`}
              size={b.size}
              top={b.top}
              left={b.left}
              speed={b.speed}
              mobileHide={b.mobileHide}
              variant={b.variant}
              parallaxRef={bubbleRefs.current[i]}
            />
          ))}
          {heroExtraBubbles.map((b, i) => (
            <ParallaxBubble
              key={`hero-extra-${i}`}
              size={b.size}
              top={b.top}
              left={b.left}
              speed={b.speed}
              variant={b.variant}
              visibilityClass="hidden lg:flex"
              parallaxRef={bubbleRefs.current[bubbleConfigs.length + i]}
            />
          ))}
          {tabletBubbles.map((b, i) => (
            <ParallaxBubble
              key={`tablet-${i}`}
              size={b.size}
              top={b.top}
              left={b.left}
              speed={b.speed}
              variant={b.variant}
              visibilityClass="hidden md:flex lg:hidden"
              parallaxRef={bubbleRefs.current[bubbleConfigs.length + heroExtraBubbles.length + i]}
            />
          ))}
          {mobileBubbles.map((b, i) => (
            <ParallaxBubble
              key={`mobile-${i}`}
              size={b.size}
              top={b.top}
              left={b.left}
              speed={b.speed}
              variant={b.variant}
              visibilityClass="flex md:hidden"
              parallaxRef={bubbleRefs.current[bubbleConfigs.length + heroExtraBubbles.length + tabletBubbles.length + i]}
            />
          ))}
        </div>

        {/* Header — logo left, nav center, actions right */}
        <header className="relative z-50 w-full py-5 px-4 md:px-8 flex items-center justify-between gap-4" role="banner">
          {/* Left: logo */}
          <div className="flex items-center">
            <GratimyLogo variant="full" size={34} />
          </div>

          {/* Center: nav (desktop only) */}
          <nav className="hidden lg:flex gap-7 items-center absolute left-1/2 -translate-x-1/2" aria-label="Main navigation">
            <a href="#about" className="text-white/85 hover:text-white font-medium transition-colors text-sm font-montserrat">{t('landing.nav.about')}</a>
            <a href="#features" className="text-white/85 hover:text-white font-medium transition-colors text-sm font-montserrat">{t('landing.nav.features')}</a>
            <a href="#how-it-works" className="text-white/85 hover:text-white font-medium transition-colors text-sm font-montserrat">{t('landing.nav.howItWorks')}</a>
            <a href="/pricing" className="text-white/85 hover:text-white font-medium transition-colors text-sm font-montserrat">{t('landing.nav.pricing')}</a>
          </nav>

          {/* Right: actions (desktop) */}
          <div className="hidden lg:flex items-center gap-3 justify-end">
            <LanguageSelector />
            <a
              href="/login"
              className="inline-flex items-center justify-center text-white border border-white/40 hover:bg-white/10 px-5 py-2 rounded-full text-sm font-semibold transition-colors font-montserrat"
              data-testid="button-sign-in"
            >
              {t('ui.signIn')}
            </a>
            <a
              href="/login?signup=true"
              className="inline-flex items-center justify-center bg-white text-[#6B46C1] hover:bg-white/90 px-5 py-2 rounded-full text-sm font-semibold transition-colors font-montserrat shadow-[0_4px_14px_rgba(124,159,247,0.35)]"
              data-testid="button-header-get-started"
            >
              {t('landing.nav.getStartedBtn')}
            </a>
          </div>

          {/* Mobile: hamburger right */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center"
            aria-label="Toggle mobile menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
          </button>
        </header>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 gradient-hero backdrop-blur-xl flex flex-col items-center justify-center">
            <GratimyLogo variant="light" size={40} className="mb-10" />
            <nav className="text-center space-y-7 mb-10">
              {[
                { label: t('landing.nav.about'), href: '#about' },
                { label: t('landing.nav.features'), href: '#features' },
                { label: t('landing.nav.howItWorks'), href: '#how-it-works' },
                { label: t('landing.nav.pricing'), href: '/pricing' },
              ].map((item) => (
                <div key={item.label}>
                  <a
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-2xl font-medium text-white/90 hover:text-white block"
                  >
                    {item.label}
                  </a>
                </div>
              ))}
            </nav>
            <div className="flex flex-col items-center gap-4">
              <LanguageSelector />
              <a
                href="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="inline-flex items-center justify-center text-white border border-white/40 hover:bg-white/10 px-5 py-2 rounded-full text-sm font-semibold transition-colors font-montserrat"
              >
                {t('ui.signIn')}
              </a>
              <a
                href="/login?signup=true"
                onClick={() => setIsMobileMenuOpen(false)}
                className="inline-flex items-center justify-center bg-white text-[#6B46C1] hover:bg-white/90 px-5 py-2 rounded-full font-semibold text-sm transition-colors font-montserrat"
              >
                {t('landing.nav.getStartedBtn')}
              </a>
            </div>
          </div>
        )}

        {/* Hero content — centered stack */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-8 pt-10 pb-32 sm:pt-16 sm:pb-40">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-7 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white/95 text-xs sm:text-sm font-medium font-montserrat shadow-[0_4px_18px_rgba(124,159,247,0.25)]">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t('landing.hero.badge')}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white mb-5 leading-tight font-ztgatha font-semibold max-w-4xl"
              style={{ textShadow: '0 2px 24px rgba(61,31,122,0.25)' }}>
            {t('landing.hero.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-base sm:text-lg md:text-xl text-white/85 mb-9 max-w-xl leading-relaxed font-montserrat">
            {t('landing.hero.subtitle')}
          </p>

          {/* Primary CTA + secondary link */}
          <div className="flex flex-col items-center gap-4">
            <a
              href="/login?signup=true"
              className="inline-flex items-center justify-center bg-white text-[#6B46C1] hover:bg-white/95 px-7 py-3 rounded-full font-semibold text-base transition-all font-montserrat shadow-[0_8px_24px_rgba(61,31,122,0.25)] hover:-translate-y-0.5"
              data-testid="button-hero-cta"
            >
              {t('landing.hero.ctaPrimary')}
            </a>
            <a
              href="#how-it-works"
              className="text-white/85 hover:text-white font-medium text-sm font-montserrat underline-offset-4 hover:underline transition-colors"
            >
              {t('landing.seeHowItWorks')}
            </a>
          </div>

          {/* Value pills */}
          <div className="mt-14 sm:mt-20 flex flex-wrap items-center justify-center gap-3 sm:gap-4 max-w-3xl">
            {[
              { label: t('landing.hero.value1'), variant: 'blue' as BubbleVariant },
              { label: t('landing.hero.value2'), variant: 'pink' as BubbleVariant },
              { label: t('landing.hero.value3'), variant: 'gradient' as BubbleVariant },
            ].map((pill) => (
              <div
                key={pill.label}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white text-xs sm:text-sm font-medium font-montserrat shadow-[0_4px_18px_rgba(124,159,247,0.18)]"
              >
                <img
                  src={BUBBLE_IMAGES[pill.variant]}
                  alt=""
                  aria-hidden="true"
                  className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                  draggable={false}
                />
                <span>{pill.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom fade overlay → page surface */}
        <div className="gradient-hero-fade pointer-events-none absolute inset-x-0 bottom-0 h-40 sm:h-56 z-[5]" aria-hidden="true" />
      </div>

      {/* ===== THE GRATIMY BOOK — product showcase section ===== */}
      <section className="py-20 md:py-24" style={{ background: 'var(--color-surface-page, #FFF4FA)' }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full bg-white border text-xs font-montserrat font-semibold uppercase tracking-wider"
            style={{ borderColor: 'rgba(124,159,247,0.35)', color: 'var(--color-violet-700)' }}
          >
            <span>{t('landing.gratimyBook.badge')}</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl mb-4 font-ztgatha font-bold leading-tight"
            style={{ color: 'var(--color-violet-900)' }}
          >
            {t('landing.gratimyBook.title')}
          </h2>
          <p
            className="text-base md:text-lg font-montserrat max-w-2xl mx-auto"
            style={{ color: 'var(--color-violet-500)' }}
          >
            {t('landing.gratimyBook.subtitle')}
          </p>
          <div
            className="mt-10 md:mt-14 mx-auto overflow-hidden rounded-2xl"
            style={{
              maxWidth: 1100,
              boxShadow: '0 24px 60px rgba(107,70,193,0.16), 0 8px 20px rgba(124,159,247,0.12)',
              WebkitMaskImage: 'linear-gradient(to bottom, #000 0%, #000 70%, transparent 100%)',
              maskImage: 'linear-gradient(to bottom, #000 0%, #000 70%, transparent 100%)',
            }}
          >
            <img
              src={gratimyDashboardTilted}
              alt="The Gratimy dashboard"
              className="block w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* ===== STATS BAND + CREATE UNIQUE MEMORIES — about section ===== */}
      <div
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #FFF4FA 0%, #FFE8F4 35%, #FFD9EC 65%, #E6CFF1 100%)' }}
      >
        {sectionBubbles.slice(0, 2).map((b, i) => (
          <ParallaxBubble
            key={`stats-bubble-${i}`}
            size={b.size}
            top={b.top}
            left={b.left}
            speed={b.speed}
            variant={b.variant}
            visibilityClass="hidden md:flex"
            parallaxRef={bubbleRefs.current[bubbleConfigs.length + heroExtraBubbles.length + tabletBubbles.length + mobileBubbles.length + i]}
          />
        ))}

        {/* ===== STATS BAND ===== */}
        <section className="relative pt-16 pb-10 md:pt-20 md:pb-14 px-4 md:px-8">
          <div className="max-w-6xl mx-auto relative z-10">
            {/* Desktop: 3 columns. Mobile: stacked. */}
            <div className="hidden md:grid md:grid-cols-3 md:gap-10 items-start">
              {/* Column 1 — Headline */}
              <p className="font-ztgatha text-violet-900 text-2xl leading-snug" style={{ color: 'var(--color-violet-900)' }}>
                {t('landing.stats.heading')}
              </p>

              {/* Column 2 — 80% + $500 → $3,055 */}
              <div className="space-y-8">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-ztgatha font-bold text-5xl" style={{ color: 'var(--color-violet-700)' }}>
                      {t('landing.stats.stat1Number')}
                    </span>
                    <span className="font-ztgatha font-semibold text-2xl" style={{ color: 'var(--color-violet-700)' }}>%</span>
                  </div>
                  <p className="font-montserrat font-semibold text-sm mt-1" style={{ color: 'var(--color-violet-900)' }}>
                    {t('landing.stats.stat1Title')}
                  </p>
                  <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                    {t('landing.stats.stat1Footnote')}
                  </p>
                </div>
                <div>
                  <div className="font-ztgatha font-extrabold text-6xl lg:text-7xl leading-none tracking-tight" style={{ color: 'var(--color-violet-700)' }}>
                    {t('landing.stats.stat2Number', { startAmount, endAmount })}
                  </div>
                  <p className="font-montserrat font-semibold text-sm mt-2" style={{ color: 'var(--color-violet-900)' }}>
                    {t('landing.stats.stat2Title')}
                  </p>
                  <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                    {t('landing.stats.stat2Footnote')}
                  </p>
                </div>
              </div>

              {/* Column 3 — 20% + Gratimy */}
              <div className="space-y-8">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="font-ztgatha font-bold text-5xl" style={{ color: 'var(--color-violet-700)' }}>
                      {t('landing.stats.stat3Number')}
                    </span>
                    <span className="font-ztgatha font-semibold text-2xl" style={{ color: 'var(--color-violet-700)' }}>%</span>
                  </div>
                  <p className="font-montserrat font-semibold text-sm mt-1" style={{ color: 'var(--color-violet-900)' }}>
                    {t('landing.stats.stat3Title')}
                  </p>
                  <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                    {t('landing.stats.stat3Subtitle')}
                  </p>
                </div>
                <div>
                  <div className="font-ztgatha font-bold text-4xl" style={{ color: 'var(--color-violet-700)' }}>
                    {t('landing.stats.brandTitle')}
                  </div>
                  <p className="font-montserrat font-semibold text-sm mt-1" style={{ color: 'var(--color-violet-900)' }}>
                    {t('landing.stats.brandSubtitle')}
                  </p>
                  <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                    {t('landing.stats.brandFootnote')}
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile: swipeable carousel through all stats. */}
            <div className="md:hidden flex flex-col items-center text-center">
              <p className="font-ztgatha text-violet-900 text-lg leading-snug max-w-md" style={{ color: 'var(--color-violet-900)' }}>
                {t('landing.stats.heading')}
              </p>
              <div className="mt-7 w-full max-w-sm">
                <MobileCarousel
                  ariaLabel={t('landing.stats.heading')}
                  slideAriaLabel={(i) => `Stat ${i + 1} of 4`}
                  items={[
                    (
                      <div key="stat1" className="px-4">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="font-ztgatha font-bold text-5xl sm:text-6xl leading-none" style={{ color: 'var(--color-violet-700)' }}>
                            {t('landing.stats.stat1Number')}
                          </span>
                          <span className="font-ztgatha font-semibold text-2xl" style={{ color: 'var(--color-violet-700)' }}>%</span>
                        </div>
                        <p className="font-montserrat font-semibold text-sm mt-3" style={{ color: 'var(--color-violet-900)' }}>
                          {t('landing.stats.stat1Title')}
                        </p>
                        <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                          {t('landing.stats.stat1Footnote')}
                        </p>
                      </div>
                    ),
                    (
                      <div key="stat3" className="px-4">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="font-ztgatha font-bold text-5xl sm:text-6xl leading-none" style={{ color: 'var(--color-violet-700)' }}>
                            {t('landing.stats.stat3Number')}
                          </span>
                          <span className="font-ztgatha font-semibold text-2xl" style={{ color: 'var(--color-violet-700)' }}>%</span>
                        </div>
                        <p className="font-montserrat font-semibold text-sm mt-3" style={{ color: 'var(--color-violet-900)' }}>
                          {t('landing.stats.stat3Title')}
                        </p>
                        <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                          {t('landing.stats.stat3Subtitle')}
                        </p>
                      </div>
                    ),
                    (
                      <div key="stat2" className="px-4">
                        <div className="font-ztgatha font-extrabold text-5xl sm:text-6xl leading-none tracking-tight" style={{ color: 'var(--color-violet-700)' }}>
                          {t('landing.stats.stat2Number', { startAmount, endAmount })}
                        </div>
                        <p className="font-montserrat font-semibold text-sm mt-3" style={{ color: 'var(--color-violet-900)' }}>
                          {t('landing.stats.stat2Title')}
                        </p>
                        <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                          {t('landing.stats.stat2Footnote')}
                        </p>
                      </div>
                    ),
                    (
                      <div key="brand" className="px-4">
                        <div className="font-ztgatha font-bold text-4xl sm:text-5xl leading-none" style={{ color: 'var(--color-violet-700)' }}>
                          {t('landing.stats.brandTitle')}
                        </div>
                        <p className="font-montserrat font-semibold text-sm mt-3" style={{ color: 'var(--color-violet-900)' }}>
                          {t('landing.stats.brandSubtitle')}
                        </p>
                        <p className="font-montserrat text-xs mt-0.5" style={{ color: 'var(--color-violet-500)' }}>
                          {t('landing.stats.brandFootnote')}
                        </p>
                      </div>
                    ),
                  ]}
                />
              </div>
            </div>

            {/* CTA — matches hero primary button */}
            <div className="mt-10 md:mt-12 flex justify-center">
              <a
                href="/login?signup=true"
                className="inline-flex items-center justify-center px-8 h-12 rounded-full text-white font-montserrat font-semibold text-base bg-[#3B82F6] hover:bg-[#2563EB] shadow-[0_10px_24px_rgba(59,130,246,0.35)] transition-colors"
                data-testid="button-stats-get-started"
              >
                {t('landing.stats.cta')}
              </a>
            </div>

            {/* Footnote ² */}
            <p
              className="mt-8 md:mt-10 max-w-3xl mx-auto text-center font-montserrat text-[11px] md:text-xs leading-relaxed"
              style={{ color: 'var(--color-violet-500)' }}
            >
              {t('landing.stats.footnote2', { startAmount, endAmount })}
            </p>
          </div>
        </section>

        {/* Dotted divider — desktop only */}
        <div className="hidden md:block max-w-6xl mx-auto px-8">
          <div
            className="h-px w-full"
            style={{ borderTop: '1px dashed rgba(124,159,247,0.45)' }}
            aria-hidden="true"
          />
        </div>

        {/* ===== CREATE UNIQUE MEMORIES BLOCK ===== */}
        <section id="about" className="relative pt-14 pb-24 md:pt-16 md:pb-28 px-4 md:px-8">
          {sectionBubbles.slice(5, 7).map((b, i) => (
            <ParallaxBubble
              key={`memories-bubble-${i}`}
              size={b.size}
              top={b.top}
              left={b.left}
              speed={b.speed}
              variant={b.variant}
              visibilityClass="hidden md:flex"
              parallaxRef={bubbleRefs.current[bubbleConfigs.length + heroExtraBubbles.length + tabletBubbles.length + mobileBubbles.length + 5 + i]}
            />
          ))}

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-10 md:mb-14">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold font-montserrat mb-5"
                style={{ background: 'rgba(124,159,247,0.18)', color: 'var(--color-violet-700)' }}
              >
                <span aria-hidden="true">🧡</span>
                <span>{t('landing.memories.pill')}</span>
              </div>
              <h2 className="font-ztgatha font-bold text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight" style={{ color: 'var(--color-violet-900)' }}>
                {t('landing.memories.title')}
              </h2>
              <p className="font-montserrat text-sm md:text-base max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                {t('landing.memoriesSubheader')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  title: t('landing.memories.card1.title'),
                  desc: t('landing.memories.card1.body'),
                  icon: <Heart className="w-6 h-6" style={{ color: 'var(--color-coral-400)' }} />,
                  accent: 'var(--color-coral-400)',
                  featured: false,
                },
                {
                  title: t('landing.memories.card2.title'),
                  desc: t('landing.memories.card2.body'),
                  icon: <Target className="w-6 h-6" style={{ color: 'var(--color-violet-700)' }} />,
                  accent: 'var(--color-violet-700)',
                  featured: true,
                },
                {
                  title: t('landing.memories.card3.title'),
                  desc: t('landing.memories.card3.body'),
                  icon: <CalendarDays className="w-6 h-6" style={{ color: 'var(--color-royalblue-500)' }} />,
                  accent: 'var(--color-royalblue-500)',
                  featured: false,
                },
              ].map(({ title, desc, icon, accent }) => (
                <div
                  key={title}
                  className="bg-white p-6 md:p-7"
                  style={{
                    borderRadius: 'var(--radius-card)',
                    boxShadow: 'var(--shadow-card-default)',
                  }}
                >
                  <div className="mb-10">{icon}</div>
                  <h3 className="font-ztgatha font-semibold text-lg mb-3" style={{ color: accent }}>
                    {title}
                  </h3>
                  <p className="font-montserrat text-sm leading-relaxed" style={{ color: 'var(--color-violet-700)' }}>
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      {/* ===== WHY CHOOSE + HOW IT WORKS — continuous light surface ===== */}
      <div
        className="relative overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #E6CFF1 0%, #EDD9F2 30%, #E4D6F3 60%, #DCD4F2 100%)' }}
      >
        {/* ===== WHY CHOOSE GRATIMY ===== */}
        <section className="relative py-20 md:py-24 px-4 md:px-8">
          {sectionBubbles.slice(2, 4).map((b, i) => (
            <ParallaxBubble
              key={`why-bubble-${i}`}
              size={b.size}
              top={b.top}
              left={b.left}
              speed={b.speed}
              variant={b.variant}
              visibilityClass="hidden md:flex"
              parallaxRef={bubbleRefs.current[bubbleConfigs.length + heroExtraBubbles.length + tabletBubbles.length + mobileBubbles.length + 2 + i]}
            />
          ))}

          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-12 md:mb-14">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold font-montserrat mb-5"
                style={{ background: 'rgba(124,159,247,0.18)', color: 'var(--color-violet-700)' }}
              >
                <span aria-hidden="true">🧡</span>
                <span>{t('landing.why.pill')}</span>
              </div>
              <h2 className="font-ztgatha font-bold text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight" style={{ color: 'var(--color-violet-900)' }}>
                {t('app.why.title')}
              </h2>
              <p className="font-montserrat text-sm md:text-base max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                {t('landing.why.subheader')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {[
                { title: t('app.why.reason1.title'), desc: t('app.why.reason1.description'), icon: <Heart className="w-5 h-5" style={{ color: 'var(--color-royalblue-500)' }} /> },
                { title: t('app.why.reason2.title'), desc: t('app.why.reason2.description'), icon: <Gift className="w-5 h-5" style={{ color: 'var(--color-royalblue-500)' }} /> },
                { title: t('app.why.reason3.title'), desc: t('app.why.reason3.description'), icon: <Users className="w-5 h-5" style={{ color: 'var(--color-royalblue-500)' }} /> },
                { title: t('app.why.reason4.title'), desc: t('app.why.reason4.description'), icon: <Shield className="w-5 h-5" style={{ color: 'var(--color-royalblue-500)' }} /> },
              ].map(({ title, desc, icon }) => (
                <div
                  key={title}
                  className="bg-white p-6 md:p-7"
                  style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card-default)' }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(124,159,247,0.15)' }}
                    >
                      {icon}
                    </div>
                    <div>
                      <h3 className="font-ztgatha font-semibold text-base md:text-lg mb-2" style={{ color: 'var(--color-violet-900)' }}>
                        {title}
                      </h3>
                      <p className="font-montserrat text-sm leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section id="how-it-works" className="relative py-20 md:py-24 px-4 md:px-8">
          <div className="max-w-5xl mx-auto relative z-10">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="font-ztgatha font-bold text-3xl sm:text-4xl md:text-5xl mb-4 leading-tight" style={{ color: 'var(--color-violet-900)' }}>
                {t('landing.howItWorksTitle')}
              </h2>
              <p className="font-montserrat text-sm md:text-base max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                {t('landing.howItWorksSubtitle')}
              </p>
            </div>

            {/* Desktop: 3-step row with dashed connector */}
            <div className="hidden md:block">
              <div className="relative max-w-3xl mx-auto mb-6">
                {/* dashed connector behind icons */}
                <div className="absolute top-1/2 left-0 right-0 px-[16.6%] -translate-y-1/2" aria-hidden="true">
                  <div className="h-px w-full" style={{ borderTop: '1.5px dashed rgba(107,70,193,0.35)' }} />
                </div>
                <div className="relative grid grid-cols-3 gap-8">
                  {[
                    { icon: <Sparkles className="w-5 h-5" style={{ color: 'var(--color-violet-700)' }} />, filled: false },
                    { icon: <Heart className="w-6 h-6 text-white" />, filled: true },
                    { icon: <Users className="w-5 h-5" style={{ color: 'var(--color-violet-700)' }} />, filled: false },
                  ].map(({ icon, filled }, i) => (
                    <div key={i} className="flex justify-center">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={filled
                          ? { background: 'var(--color-violet-700)', boxShadow: '0 10px 24px rgba(107,70,193,0.35)' }
                          : { background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(124,159,247,0.35)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                      >
                        {icon}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-6 max-w-4xl mx-auto">
                {[
                  { step: '1', title: t('landing.step1Title'), desc: t('landing.step1Desc') },
                  { step: '2', title: t('landing.step2Title'), desc: t('landing.step2Desc') },
                  { step: '3', title: t('landing.step3Title'), desc: t('landing.step3Desc') },
                ].map(({ step, title, desc }) => (
                  <div
                    key={step}
                    className="text-center bg-white p-6"
                    style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card-default)' }}
                  >
                    <div className="font-montserrat text-xs mb-2" style={{ color: 'var(--color-violet-500)' }}>
                      {t('landing.stepLabel', { n: step })}
                    </div>
                    <h3 className="font-ztgatha font-semibold text-lg mb-3 leading-snug" style={{ color: 'var(--color-violet-900)' }}>
                      {title}
                    </h3>
                    <p className="font-montserrat text-sm leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                      {desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile: swipeable carousel through all three steps. */}
            <div className="md:hidden max-w-sm mx-auto">
              <MobileCarousel
                ariaLabel={t('landing.howItWorksTitle')}
                slideAriaLabel={(i) => `${t('landing.stepLabel', { n: String(i + 1) })}`}
                items={[
                  { step: '1', title: t('landing.step1Title'), desc: t('landing.step1Desc'), icon: <Sparkles className="w-5 h-5" style={{ color: 'var(--color-violet-700)' }} />, filled: false },
                  { step: '2', title: t('landing.step2Title'), desc: t('landing.step2Desc'), icon: <Heart className="w-6 h-6 text-white" />, filled: true },
                  { step: '3', title: t('landing.step3Title'), desc: t('landing.step3Desc'), icon: <Users className="w-5 h-5" style={{ color: 'var(--color-violet-700)' }} />, filled: false },
                ].map(({ step, title, desc, icon, filled }) => (
                  <div key={step} className="px-1">
                    <div className="flex justify-center mb-5">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={filled
                          ? { background: 'var(--color-violet-700)', boxShadow: '0 10px 24px rgba(107,70,193,0.35)' }
                          : { background: 'rgba(255,255,255,0.85)', border: '1px solid rgba(124,159,247,0.35)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                      >
                        {icon}
                      </div>
                    </div>
                    <div
                      className="bg-white p-6 text-center"
                      style={{ borderRadius: 'var(--radius-card)', boxShadow: 'var(--shadow-card-default)' }}
                    >
                      <div className="font-montserrat text-xs mb-2" style={{ color: 'var(--color-violet-500)' }}>
                        {t('landing.stepLabel', { n: step })}
                      </div>
                      <h3 className="font-ztgatha font-semibold text-lg mb-3 leading-snug" style={{ color: 'var(--color-violet-900)' }}>
                        {title}
                      </h3>
                      <p className="font-montserrat text-sm leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                        {desc}
                      </p>
                    </div>
                  </div>
                ))}
              />
            </div>

            <div className="text-center mt-12 md:mt-14">
              <a
                href="/login?signup=true"
                className="text-white px-6 py-2.5 rounded-full font-semibold text-sm transition-colors inline-flex items-center gap-2 font-montserrat hover:brightness-110"
                style={{ background: 'var(--color-royalblue-500)' }}
              >
                {t('landing.howItWorksCta')}
              </a>
            </div>
          </div>
        </section>
      </div>

      {/* ===== SHOW THE IMPACT — interactive demo ===== */}
      <ImpactSection />

      {/* ===== TESTIMONIALS / REVIEWS ===== */}
      <section className="py-20 md:py-24" style={{ background: 'var(--color-surface-page, #FFF4FA)' }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-14">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 mb-5 rounded-full bg-white border text-xs font-montserrat font-semibold uppercase tracking-wider"
              style={{ borderColor: 'rgba(124,159,247,0.35)', color: 'var(--color-violet-700)' }}
            >
              <Star className="w-3.5 h-3.5 fill-[#FFC23B] text-[#FFC23B]" />
              <span>{t('landing.reviews.badge')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl mb-4 font-ztgatha font-bold leading-tight" style={{ color: 'var(--color-violet-900)' }}>
              {t('landing.testimonialsTitle')}
            </h2>
            <p className="text-base md:text-lg font-montserrat max-w-2xl mx-auto" style={{ color: 'var(--color-violet-500)' }}>
              {t('landing.testimonialsSubtitle')}
            </p>
          </div>

          {reviewsError && !reviewsData ? (
            <p className="text-center font-montserrat" style={{ color: 'var(--color-violet-500)' }}>
              {t('landing.reviews.loadError')}
            </p>
          ) : reviewsExpanded ? (
            // ===== Expanded horizontal carousel =====
            <div className="relative">
              <button
                type="button"
                onClick={() => scrollReviews(-1)}
                aria-label={t('landing.reviews.scrollLeft')}
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 w-11 h-11 items-center justify-center rounded-full bg-white border z-10 hover:bg-violet-50 transition-colors"
                style={{ borderColor: 'rgba(124,159,247,0.35)', color: 'var(--color-violet-700)', boxShadow: '0 4px 14px rgba(124,159,247,0.18)' }}
                data-testid="reviews-scroll-left"
              >
                <ArrowRight className="w-5 h-5 rotate-180" />
              </button>
              <button
                type="button"
                onClick={() => scrollReviews(1)}
                aria-label={t('landing.reviews.scrollRight')}
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 w-11 h-11 items-center justify-center rounded-full bg-white border z-10 hover:bg-violet-50 transition-colors"
                style={{ borderColor: 'rgba(124,159,247,0.35)', color: 'var(--color-violet-700)', boxShadow: '0 4px 14px rgba(124,159,247,0.18)' }}
                data-testid="reviews-scroll-right"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
              <div
                ref={reviewsScrollerRef}
                className="flex gap-5 md:gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth px-4 md:px-0 scroll-pl-4 md:scroll-pl-0 -mx-4 md:mx-0"
                style={{ scrollbarWidth: 'none' }}
              >
                {reviews.map((r) => (
                  <ReviewCard key={r.id} review={r} t={t} />
                ))}
              </div>
            </div>
          ) : (
            // ===== Collapsed: first 3 cards =====
            // Mobile: horizontal scroll snapped to the left, with leading
            // padding so card #1 lands flush with the page gutter and
            // peeks card #2 on the right.
            // Desktop: 3-column grid.
            <div
              className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory px-4 -mx-4 scroll-pl-4 md:overflow-visible md:grid md:grid-cols-3 md:gap-6 md:px-0 md:mx-0"
              style={{ scrollbarWidth: 'none' }}
            >
              {reviews.slice(0, 3).map((r) => (
                <ReviewCard key={r.id} review={r} t={t} />
              ))}
            </div>
          )}

          {/* ===== Rating summary + avatar stack + view-all CTA ===== */}
          <div className="mt-10 md:mt-12 flex flex-col items-center gap-5">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-3">
                {['/testimonials/sarah.webp', '/testimonials/mike.webp', '/testimonials/lisa.webp'].map((src) => (
                  <img
                    key={src}
                    src={src}
                    alt=""
                    aria-hidden="true"
                    className="w-9 h-9 rounded-full object-cover border-2 border-white"
                    style={{ boxShadow: '0 2px 6px rgba(124,159,247,0.25)' }}
                  />
                ))}
              </div>
              {(reviewsData?.length ?? 0) > 35 && (
                <p className="font-montserrat text-sm md:text-base font-medium" style={{ color: 'var(--color-violet-700)' }}>
                  {t('landing.reviews.ratedSummary', { rating: '4.9', count: String(reviewsData?.length ?? 0) })}
                </p>
              )}
            </div>

            {reviews.length > 3 && (
              <button
                type="button"
                onClick={() => setReviewsExpanded((v) => !v)}
                className="inline-flex items-center justify-center px-8 h-12 rounded-full text-white font-montserrat font-semibold text-base bg-[#3B82F6] hover:bg-[#2563EB] shadow-[0_10px_24px_rgba(59,130,246,0.35)] transition-colors"
                data-testid="button-toggle-reviews"
              >
                {reviewsExpanded ? t('landing.reviews.showLess') : t('landing.reviews.viewAll')}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ===== PAID FEATURES ===== */}
      <section
        id="features"
        className="relative py-20 md:py-28 px-4 md:px-8 overflow-hidden"
        style={{ background: 'linear-gradient(180deg, #FFF4FA 0%, #F4ECFB 35%, #E8DFF7 65%, #FCE8F0 100%)' }}
      >
        <div id="pricing" className="max-w-6xl mx-auto">
          {(() => {
            const featureItems = [
              { icon: Lock,     title: t('landing.feature1Title'), desc: t('landing.feature1Desc') },
              { icon: Gift,     title: t('landing.feature2Title'), desc: t('landing.feature2Desc') },
              { icon: Heart,    title: t('landing.feature3Title'), desc: t('landing.feature3Desc') },
              { icon: Users,    title: t('landing.feature4Title'), desc: t('landing.feature4Desc') },
              { icon: Sparkles, title: t('landing.feature5Title'), desc: t('landing.feature5Desc') },
              { icon: Shield,   title: t('landing.feature6Title'), desc: t('landing.feature6Desc') },
            ];
            const FeatureCard = ({ Icon, title, desc }: { Icon: typeof Lock; title: string; desc: string }) => (
              <div
                className="relative flex flex-col p-7 backdrop-blur-md transition-all hover:-translate-y-0.5"
                style={{
                  background: 'rgba(255,255,255,0.55)',
                  border: '1.5px solid rgba(255,255,255,0.9)',
                  borderRadius: 20,
                  boxShadow: '0 8px 26px rgba(124,159,247,0.18), inset 0 1px 1px rgba(255,255,255,0.65)',
                }}
              >
                <div className="mb-5">
                  <Icon className="w-7 h-7" style={{ color: 'var(--color-royal-500, #3B82F6)', strokeWidth: 1.75 }} />
                </div>
                <h3 className="font-ztgatha font-semibold text-lg mb-2" style={{ color: 'var(--color-violet-900)' }}>
                  {title}
                </h3>
                <p className="font-montserrat text-sm leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                  {desc}
                </p>
              </div>
            );

            return (
              <>
                {/* ===== Desktop: 2-column split ===== */}
                <div className="hidden md:grid md:grid-cols-2 md:gap-14 lg:gap-20 items-center">
                  {/* Left — heading + CTA */}
                  <div className="max-w-md">
                    <h2 className="font-ztgatha font-bold text-4xl lg:text-5xl mb-5 leading-tight" style={{ color: 'var(--color-violet-900)' }}>
                      {t('landing.featuresTitle')}
                    </h2>
                    <p className="font-montserrat text-base mb-6 leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                      {t('landing.featuresSubtitle')}
                    </p>
                    <p className="font-montserrat text-sm font-semibold mb-7" style={{ color: '#3B82F6' }}>
                      {t('landing.pricingNote')}
                    </p>
                    <a
                      href="/login?signup=true"
                      className="inline-flex items-center justify-center px-7 h-12 rounded-full text-white font-montserrat font-semibold text-base bg-[#3B82F6] hover:bg-[#2563EB] shadow-[0_10px_24px_rgba(59,130,246,0.35)] transition-colors"
                      data-testid="button-features-get-started"
                    >
                      {t('landing.hero.ctaPrimary')}
                    </a>
                  </div>

                  {/* Right — 3x2 glass card grid */}
                  <div className="grid grid-cols-2 gap-5 lg:gap-6">
                    {featureItems.map(({ icon: Icon, title, desc }) => (
                      <FeatureCard key={title} Icon={Icon} title={title} desc={desc} />
                    ))}
                  </div>
                </div>

                {/* ===== Mobile: centered heading + horizontal scroll + CTA ===== */}
                <div className="md:hidden">
                  <div className="text-center mb-8">
                    <h2 className="font-ztgatha font-bold text-3xl sm:text-4xl mb-4 leading-tight" style={{ color: 'var(--color-violet-900)' }}>
                      {t('landing.featuresTitle')}
                    </h2>
                    <p className="font-montserrat text-base max-w-md mx-auto leading-relaxed" style={{ color: 'var(--color-violet-500)' }}>
                      {t('landing.featuresSubtitle')}
                    </p>
                  </div>

                  <div
                    className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory px-4 -mx-4 scroll-pl-4"
                    style={{ scrollbarWidth: 'none' }}
                  >
                    {featureItems.map(({ icon: Icon, title, desc }) => (
                      <div
                        key={title}
                        className="flex-shrink-0 snap-start w-[78vw] min-w-[260px] max-w-[320px]"
                      >
                        <FeatureCard Icon={Icon} title={title} desc={desc} />
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex justify-center">
                    <a
                      href="/login?signup=true"
                      className="inline-flex items-center justify-center px-8 h-12 rounded-full text-white font-montserrat font-semibold text-base bg-[#3B82F6] hover:bg-[#2563EB] shadow-[0_10px_24px_rgba(59,130,246,0.35)] transition-colors"
                      data-testid="button-features-get-started-mobile"
                    >
                      {t('landing.hero.ctaPrimary')}
                    </a>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </section>

      {/* ===== CLOSING CTA — blush/lavender wash on top, brand hero gradient gradually
              revealed below so it blends down into the footer without a hard seam. ===== */}
      <section
        className="relative py-24 md:py-28 px-4 md:px-8 overflow-hidden"
        style={{
          background:
            'linear-gradient(180deg, #FCE8F0 0%, #F4ECFB 18%, #E8DFF7 32%, rgba(232,223,247,0.85) 50%, rgba(232,223,247,0.35) 78%, rgba(232,223,247,0) 100%), linear-gradient(90deg, #6F8EDC 0%, #D689AC 100%)',
        }}
      >
        <div className="max-w-3xl mx-auto text-center">
          {/* Join badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-7 backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.6)',
              border: '1px solid rgba(255,255,255,0.9)',
              boxShadow: '0 4px 14px rgba(124,159,247,0.18)',
            }}
          >
            <Gift className="w-4 h-4" style={{ color: '#F97316' }} />
            <span className="font-montserrat font-semibold text-sm" style={{ color: 'var(--color-violet-900)' }}>
              {t('landing.joinBadge')}
            </span>
          </div>

          <h2
            className="font-ztgatha font-bold text-4xl md:text-5xl lg:text-6xl mb-5 leading-tight"
            style={{ color: 'var(--color-violet-900)' }}
          >
            {t('landing.ctaTitle')}
          </h2>
          <p
            className="font-montserrat text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed"
            style={{ color: 'var(--color-violet-500)' }}
          >
            {t('landing.ctaDescription')}
          </p>

          {/* Trust badges */}
          <div className="flex flex-nowrap justify-center items-start gap-4 sm:gap-8 md:gap-10 mb-10">
            {[
              { Icon: Shield, label: t('landing.trialBadge') },
              { Icon: Lock,   label: t('landing.privacyFirst') },
              { Icon: Clock,  label: t('landing.cancelAnytime') },
            ].map(({ Icon, label }) => (
              <div key={label} className="flex flex-col md:flex-row items-center gap-2 text-center md:text-left">
                <Icon className="w-5 h-5" style={{ color: '#3B82F6', strokeWidth: 1.75 }} />
                <span
                  className="font-montserrat font-semibold text-sm md:text-base max-w-[6rem] md:max-w-none"
                  style={{ color: 'var(--color-violet-900)' }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* White pill CTA */}
          <a
            href="/login?signup=true"
            className="inline-flex items-center justify-center px-8 h-12 rounded-full font-montserrat font-semibold text-base bg-white hover:bg-white/90 transition-colors"
            style={{
              color: 'var(--color-violet-900)',
              boxShadow: '0 10px 28px rgba(124,159,247,0.28), inset 0 1px 1px rgba(255,255,255,0.9)',
              border: '1px solid rgba(255,255,255,0.9)',
            }}
            data-testid="button-cta-create-account"
          >
            {t('landing.ctaButton')}
          </a>
        </div>

        {/* ===== Give the gift of Gratimy — glass card with bubbles ===== */}
        <div id="gift" className="relative max-w-5xl mx-auto mt-20 md:mt-24 px-2 md:px-0">
          {/* Decorative bubbles behind/around the card */}
          <img
            src="/gratimy-bubble-blue.webp"
            alt=""
            aria-hidden="true"
            className="pointer-events-none select-none absolute -top-6 -right-2 md:-top-10 md:right-6 w-16 h-16 md:w-24 md:h-24 z-10"
            style={{ filter: 'drop-shadow(0 10px 28px rgba(124,159,247,0.35))' }}
          />
          <img
            src="/gratimy-bubble-blush.webp"
            alt=""
            aria-hidden="true"
            className="pointer-events-none select-none absolute -bottom-8 -left-2 md:-bottom-10 md:-left-4 w-20 h-20 md:w-28 md:h-28 z-10"
            style={{ filter: 'drop-shadow(0 10px 28px rgba(214,137,172,0.4))' }}
          />
          <img
            src="/gratimy-bubble-blue.webp"
            alt=""
            aria-hidden="true"
            className="hidden md:block pointer-events-none select-none absolute -bottom-6 right-12 w-14 h-14 z-10 opacity-80"
            style={{ filter: 'drop-shadow(0 8px 22px rgba(124,159,247,0.35))' }}
          />

          <div
            className="relative backdrop-blur-md text-center px-6 py-10 md:px-12 md:py-14"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(244,236,251,0.55) 100%)',
              border: '1.5px solid rgba(255,255,255,0.9)',
              borderRadius: 28,
              boxShadow: '0 18px 44px rgba(124,159,247,0.22), inset 0 1px 1px rgba(255,255,255,0.7)',
            }}
          >
            <h3
              className="font-ztgatha font-bold text-3xl md:text-4xl mb-4 leading-tight"
              style={{ color: 'var(--color-violet-900)' }}
            >
              {t('landing.giftTitle')}
            </h3>
            <p
              className="font-montserrat text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed"
              style={{ color: 'var(--color-violet-500)' }}
            >
              {t('landing.giftDesc')}
            </p>
            <a
              href="/pricing#gift"
              className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-full text-white font-montserrat font-semibold text-base bg-[#3B82F6] hover:bg-[#2563EB] shadow-[0_10px_24px_rgba(59,130,246,0.35)] transition-colors"
              data-testid="button-cta-send-gift"
            >
              <Gift className="w-5 h-5" strokeWidth={1.75} />
              {t('landing.giftCta')}
            </a>
          </div>
        </div>
      </section>

      {/* ===== FOOTER — pure two-tone Gratimy hero gradient (periwinkle → blush).
              The closing CTA above fades into this same gradient so the seam disappears. ===== */}
      <div style={{ background: 'linear-gradient(90deg, #6F8EDC 0%, #D689AC 100%)' }}>
        <SiteFooter />
      </div>
      </main>

    </div>
  );
}
